import React, { createContext, useContext, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, getSessionSafe, isDemoMode, disableDemoMode } from "./supabase";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  /** true enquanto a sessão está sendo carregada na inicialização */
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Loads the persisted session (with timeout: an unavailable backend must
    // not leave the UI stuck in infinite loading) and listens for changes.
    getSessionSafe().then((currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // No modo demonstracao nao ha sessao real: o INITIAL_SESSION (null) do
      // supabase-js apagaria o usuario demo logo depois do getSessionSafe.
      if (isDemoMode()) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const signOut = async (): Promise<void> => {
    if (isDemoMode()) {
      disableDemoMode();
      setUser(null);
      setSession(null);
      return;
    }
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return ctx;
}
